/**
 * A reconferência da custódia do caso (E134.F1/F2, GUS-159, ADR-0090). Espelha
 * `apps/api/types/custodia.py`.
 *
 * Conferir lê cada arquivo guardado do caso e refaz o SHA-256 do recebimento. O resultado fica
 * registrado no servidor e não se apaga; a tela relê a abertura para mostrar a situação nova.
 */
import axios, { type AxiosInstance } from 'axios';

import type { SituacaoNaConferencia } from '@/lib/abertura_do_caso';

export interface ObjetoConferidoData {
  especie: 'documento' | 'nao_examinado' | 'foto';
  objeto_id: string;
  esperado: string | null;
  obtido: string | null;
  situacao: SituacaoNaConferencia;
}

export interface ConferenciaDeIntegridadeData {
  id: string;
  case_id: string;
  usuario_id: string | null;
  origem: 'tela' | 'leitura' | 'operacao';
  criado_em: string;
  total: number;
  conferem: number;
  divergem: number;
  ausentes: number;
  sem_registro: number;
  objetos: ObjetoConferidoData[];
}

/** Por que a conferência não rodou: o caso fora do alcance, a conta sem permissão, ou falha. */
export type MotivoDaFalhaDaConferencia = 'caso_nao_encontrado' | 'sem_permissao' | 'indisponivel';

export class ConferenciaDaCustodiaError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDaConferencia) {
    super(motivo);
    this.name = 'ConferenciaDaCustodiaError';
  }
}

/** A conferência lê o caso inteiro (até centenas de MB): mais tempo que o padrão do cliente. */
export const TEMPO_DA_CONFERENCIA_MS = 2 * 60_000;

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

function motivoDoErro(err: unknown): MotivoDaFalhaDaConferencia {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  if (err.response?.status === HTTP_NOT_FOUND) return 'caso_nao_encontrado';
  if (err.response?.status === HTTP_FORBIDDEN) return 'sem_permissao';
  return 'indisponivel';
}

export async function conferirCustodia(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaDeIntegridadeData> {
  try {
    const { data } = await client.post<ConferenciaDeIntegridadeData>(
      `/casos/${caseId}/custodia/conferencias`,
      undefined,
      { timeout: TEMPO_DA_CONFERENCIA_MS },
    );
    return data;
  } catch (err) {
    throw new ConferenciaDaCustodiaError(motivoDoErro(err));
  }
}

// E136.F1/F3 (GUS-152, ADR-0092): a ocorrência de custódia e a resolução dela. Espelha
// `apps/api/types/abertura.py` (`OcorrenciaDaCustodia`) e `types/ocorrencias_de_custodia.py`.

export type AcaoDaResolucao = 'restaurado' | 'retirado_do_exame' | 'reenviado_pelo_banco';

export const ACOES_DA_RESOLUCAO: readonly AcaoDaResolucao[] = [
  'restaurado',
  'retirado_do_exame',
  'reenviado_pelo_banco',
];

export interface ResolucaoNoReciboData {
  acao: AcaoDaResolucao;
  /** O e-mail de quem resolveu; nulo quando o usuário não está no alcance de quem lê. */
  responsavel: string | null;
  nota: string | null;
  resolvida_em: string;
}

export interface OcorrenciaDaCustodiaData {
  id: string;
  especie: 'documento' | 'nao_examinado' | 'foto';
  objeto_id: string;
  /** Nulo quando o objeto não está mais vivo no caso, ou é fotografia. */
  nome: string | null;
  situacao: 'diverge' | 'ausente';
  esperado: string | null;
  obtido: string | null;
  origem: 'tela' | 'leitura' | 'operacao';
  aberta_em: string;
  aberta_por: string | null;
  resolucao: ResolucaoNoReciboData | null;
}

/** Por que a resolução não foi registrada. */
export type MotivoDaFalhaDaResolucao =
  | 'sem_restauro'
  | 'ja_resolvida'
  | 'sem_permissao'
  | 'nao_encontrada'
  | 'indisponivel';

export class ResolucaoDaOcorrenciaError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDaResolucao) {
    super(motivo);
    this.name = 'ResolucaoDaOcorrenciaError';
  }
}

const HTTP_CONFLICT = 409;

function codigoDoConflito(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail !== null && 'code' in detail) {
    return String((detail as { code: unknown }).code);
  }
  return undefined;
}

function motivoDaResolucao(err: unknown): MotivoDaFalhaDaResolucao {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  const status = err.response?.status;
  if (status === HTTP_FORBIDDEN) return 'sem_permissao';
  if (status === HTTP_NOT_FOUND) return 'nao_encontrada';
  if (status === HTTP_CONFLICT) {
    const codigo = codigoDoConflito((err.response?.data as { detail?: unknown })?.detail);
    if (codigo === 'OCORRENCIA_SEM_RESTAURO') return 'sem_restauro';
    if (codigo === 'OCORRENCIA_JA_RESOLVIDA') return 'ja_resolvida';
  }
  return 'indisponivel';
}

/** O restauro relê o arquivo: o mesmo tempo da conferência. */
export async function resolverOcorrencia(
  client: AxiosInstance,
  caseId: string,
  ocorrenciaId: string,
  pedido: { acao: AcaoDaResolucao; nota: string | null },
): Promise<void> {
  try {
    await client.post(`/casos/${caseId}/custodia/ocorrencias/${ocorrenciaId}/resolucao`, pedido, {
      timeout: TEMPO_DA_CONFERENCIA_MS,
    });
  } catch (err) {
    throw new ResolucaoDaOcorrenciaError(motivoDaResolucao(err));
  }
}
